import React, { useEffect, useMemo, useRef, useState } from 'react';
import useAppStore from '../../store/index';
import { useTeamBonus } from '../../store/hooks/useTeamBonus';
import {
    computeTeamBonusHalbjahrPool,
    TeamBonusHalbjahrKey,
    TeamBonusMemberSource,
} from '../../store/selectors/teamBonusSelectors';

interface PETeamBonusModalProps {
    isOpen: boolean;
    onClose: () => void;
    currencySymbol?: string;
}

const SOURCE_ORDER: TeamBonusMemberSource[] = [
    'personalData',
    'unproductivePersonalData',
    'azubiAssistenzData',
];

const MONTHS_HJ = 6;

const formatMoney = (n: number, symbol = '€') =>
    `${new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0)} ${symbol}`;

const perMonth = (halbjahrWert: number) => Math.round((halbjahrWert / MONTHS_HJ) * 100) / 100;

/** Zentrale Team-Bonus-Konfiguration: monatlicher Pool im Vordergrund, Multi-Select, Live-Vorschau. */
export const PETeamBonusModal: React.FC<PETeamBonusModalProps> = ({
    isOpen,
    onClose,
    currencySymbol = '€',
}) => {
    const {
        poolAmount,
        rows,
        sumSelectedGrundgehalt,
        setPoolAmount,
        toggleMember,
        setSelectedIds,
        applyShares,
    } = useTeamBonus();

    const clientData = useAppStore((s) => s.clientData);
    const settings = useAppStore((s) => s.settings);

    const [halfYear, setHalfYear] = useState<TeamBonusHalbjahrKey>('erstesHalbjahr');
    const [poolDraft, setPoolDraft] = useState<string | null>(null);
    const lastAutoKeyRef = useRef('');

    const hjPool = useMemo(
        () => computeTeamBonusHalbjahrPool(clientData, settings, halfYear),
        [clientData, settings, halfYear]
    );

    const m = useMemo(
        () => ({
            unterKostendeckung: perMonth(hjPool.defizitUnterKostendeckung),
            bisZielPolster: perMonth(hjPool.ueberschussBisZielPolster),
            ueberZiel: perMonth(hjPool.ueberschussUeberZiel),
            fehlbetrag: perMonth(hjPool.fehlbetrag),
            vkNetto: perMonth(hjPool.vkUmsatzNetto),
            earnedPool: hjPool.earnedPoolMonthly,
            earnedPoolDL: hjPool.earnedPoolDLMonthly,
            earnedPoolVK: hjPool.earnedPoolVKMonthly,
            polsterPct: hjPool.zielPolsterProzent,
            dlPct: hjPool.bonusProzent,
            vkPct: hjPool.bonusProzentVK,
        }),
        [hjPool]
    );

    // Body-Scroll sperren, solange Modal offen
    useEffect(() => {
        if (!isOpen) return;
        const prevOverflow = document.body.style.overflow;
        const prevPaddingRight = document.body.style.paddingRight;
        const scrollbarGap = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        if (scrollbarGap > 0) {
            document.body.style.paddingRight = `${scrollbarGap}px`;
        }
        return () => {
            document.body.style.overflow = prevOverflow;
            document.body.style.paddingRight = prevPaddingRight;
        };
    }, [isOpen]);

    // Autofill mit monatlichem Pool; 0 wenn Ziel nicht erreicht
    useEffect(() => {
        if (!isOpen) {
            lastAutoKeyRef.current = '';
            return;
        }
        const key = `${halfYear}:${m.earnedPool}`;
        if (lastAutoKeyRef.current === key) return;
        lastAutoKeyRef.current = key;
        setPoolAmount(m.earnedPool);
        setPoolDraft(null);
    }, [isOpen, halfYear, m.earnedPool, setPoolAmount]);

    const grouped = useMemo(() => {
        return SOURCE_ORDER.map((source) => {
            const list = rows.filter((r) => r.source === source);
            return {
                source,
                label: list[0]?.sourceLabel || source,
                list,
            };
        }).filter((g) => g.list.length > 0);
    }, [rows]);

    const selectedCount = rows.filter((r) => r.selected).length;
    const displayPool = poolDraft !== null ? poolDraft : String(poolAmount || 0);

    if (!isOpen) return null;

    const commitPool = () => {
        const raw = (poolDraft ?? String(poolAmount)).replace(',', '.');
        const n = parseFloat(raw);
        setPoolAmount(Number.isFinite(n) ? n : 0);
        setPoolDraft(null);
        applyShares();
    };

    const selectAllInGroup = (source: TeamBonusMemberSource, select: boolean) => {
        const groupIds = rows.filter((r) => r.source === source).map((r) => r.id);
        const current = new Set(rows.filter((r) => r.selected).map((r) => r.id));
        if (select) groupIds.forEach((id) => current.add(id));
        else groupIds.forEach((id) => current.delete(id));
        setSelectedIds(Array.from(current));
    };

    const hjLabel = halfYear === 'erstesHalbjahr' ? '1. Halbjahr' : '2. Halbjahr';

    return (
        <div
            className="fixed inset-0 z-[80] overflow-y-auto overscroll-contain bg-black/70 backdrop-blur-sm"
            onClick={onClose}
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
        >
            <div className="flex min-h-full items-start sm:items-center justify-center p-4 py-6">
                <div
                    className="w-full max-w-3xl rounded-2xl border border-pe-border dark:border-slate-700 bg-pe-card dark:bg-[#0f1115] shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="pe-team-bonus-title"
                >
                    <div className="flex items-center justify-between px-6 py-4 border-b border-pe-border dark:border-slate-800 bg-pe-tile-header dark:bg-[#15171e] rounded-t-2xl sticky top-0 z-10">
                        <div>
                            <p className="text-xs font-black uppercase tracking-widest text-amber-500 mb-1">Team-Leistungslohn</p>
                            <h2 id="pe-team-bonus-title" className="text-xl font-black text-pe-text dark:text-white tracking-tight">
                                PE-TeamBonus
                            </h2>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-10 h-10 text-xl rounded-lg border border-pe-border dark:border-slate-700 text-pe-muted hover:text-pe-text hover:bg-pe-elevated dark:hover:bg-slate-800"
                            aria-label="Schließen"
                        >
                            ×
                        </button>
                    </div>

                    <div className="px-6 py-3 border-b border-pe-border dark:border-slate-800 flex flex-wrap items-center gap-2">
                        <span className="text-xs font-bold uppercase tracking-widest text-pe-muted mr-1">Auswertung</span>
                        {(
                            [
                                ['erstesHalbjahr', '1. Halbjahr'],
                                ['zweitesHalbjahr', '2. Halbjahr'],
                            ] as const
                        ).map(([key, label]) => {
                            const active = halfYear === key;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setHalfYear(key)}
                                    className={`px-3.5 py-2 text-xs font-black uppercase tracking-widest rounded-lg border transition-all ${
                                        active
                                            ? 'bg-amber-500 text-black border-amber-500'
                                            : 'border-pe-border dark:border-slate-700 text-pe-muted hover:text-pe-text'
                                    }`}
                                >
                                    {label}
                                </button>
                            );
                        })}
                        <span className="text-xs text-pe-muted ml-auto">
                            {hjLabel} · Werte pro Monat
                        </span>
                    </div>

                    {/* Polster immer sichtbar — Entscheidungsbasis auch ohne Zielerreichung */}
                    {(m.bisZielPolster > 0 || m.unterKostendeckung > 0) && (
                        <div className="mx-6 mt-4 rounded-xl border border-pe-border dark:border-slate-700 bg-pe-elevated/50 dark:bg-slate-900/40 px-4 py-3.5">
                            <div className="text-xs font-bold uppercase tracking-widest text-pe-muted mb-2">
                                Unternehmens-Polster · pro Monat
                            </div>
                            {m.unterKostendeckung > 0 ? (
                                <p className="text-base text-red-700 dark:text-red-300 leading-relaxed">
                                    Unter Kostendeckung:{' '}
                                    <span className="font-mono font-bold">{formatMoney(m.unterKostendeckung, currencySymbol)}</span>
                                    {' '}/ Monat — kein gesundes Polster vorhanden.
                                </p>
                            ) : (
                                <div className="space-y-1">
                                    <div className="flex flex-wrap justify-between gap-2 text-base">
                                        <span className="text-pe-text dark:text-slate-200">
                                            Gebildetes Polster bis {m.polsterPct.toFixed(0)} %-Ziel
                                        </span>
                                        <span className="font-mono font-bold text-amber-600 dark:text-amber-400">
                                            {formatMoney(m.bisZielPolster, currencySymbol)} / Monat
                                        </span>
                                    </div>
                                    <p className="text-sm text-pe-muted leading-snug">
                                        Das sind die Rücklagen für ein gesundes Unternehmen. Von diesem Polster kannst du
                                        unten freiwillig einen Bonuspool festlegen — auch wenn das Gesamtziel noch nicht
                                        erreicht ist.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {!hjPool.targetReached ? (
                        <div className="mx-6 mt-3 rounded-xl border border-amber-500/50 bg-amber-500/10 px-4 py-3.5 text-base text-amber-900 dark:text-amber-200 leading-relaxed">
                            Gesamtziel noch nicht erreicht. Es fehlen{' '}
                            <span className="font-bold font-mono">{formatMoney(m.fehlbetrag, currencySymbol)}</span>
                            {' '}/ Monat bis zum Ziel inkl. {m.polsterPct.toFixed(0)} %-Polster.
                            {' '}Automatischer Team-Bonus (DL + Verkauf) = 0 — Auszahlung nur, wenn du unten manuell etwas einträgst.
                        </div>
                    ) : (
                        <div className="mx-6 mt-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-4 text-base text-emerald-950 dark:text-emerald-100 leading-relaxed space-y-3">
                            <p className="font-bold text-lg">Gesamtziel erreicht ({hjLabel}) — Vorschlag Team-Bonuspool</p>
                            <div className="space-y-2 font-mono text-base">
                                <div className="flex flex-wrap justify-between gap-2">
                                    <span>Überschuss über das Ziel</span>
                                    <span className="font-bold">{formatMoney(m.ueberZiel, currencySymbol)} / Monat</span>
                                </div>
                                <div className="flex flex-wrap justify-between gap-2 text-sm sm:text-base opacity-95 pl-1 border-l-2 border-emerald-500/40">
                                    <span>DL-Bonus ({m.dlPct.toFixed(0)} % vom Überschuss)</span>
                                    <span className="font-bold">{formatMoney(m.earnedPoolDL, currencySymbol)}</span>
                                </div>
                                <div className="flex flex-wrap justify-between gap-2 text-sm sm:text-base opacity-95 pl-1 border-l-2 border-emerald-500/40">
                                    <span>
                                        Verkaufsbonus ({m.vkPct.toFixed(0)} % von VK{' '}
                                        {formatMoney(m.vkNetto, currencySymbol)})
                                    </span>
                                    <span className="font-bold">{formatMoney(m.earnedPoolVK, currencySymbol)}</span>
                                </div>
                                <div className="flex flex-wrap justify-between gap-2 pt-2 border-t border-emerald-500/30 font-bold">
                                    <span>Team-Bonuspool gesamt</span>
                                    <span>{formatMoney(m.earnedPool, currencySymbol)} / Monat</span>
                                </div>
                            </div>
                            <p className="text-sm opacity-90 leading-snug">
                                Das Polster bis {m.polsterPct.toFixed(0)} % (
                                {formatMoney(m.bisZielPolster, currencySymbol)} / Monat) bleibt im Unternehmen und fließt
                                nicht in den Bonus. Pool unten ist vorbefüllt und manuell änderbar.
                            </p>
                        </div>
                    )}

                    <div className="px-6 py-4 border-y border-pe-border dark:border-slate-800 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="sm:col-span-1">
                            <label className="text-xs font-bold uppercase tracking-widest text-amber-600 dark:text-amber-400 block mb-2">
                                Bonuspool / Monat
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={displayPool}
                                    onChange={(e) => setPoolDraft(e.target.value)}
                                    onBlur={commitPool}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') commitPool();
                                    }}
                                    className="w-full bg-pe-input dark:bg-[#08090b] border border-amber-500/40 text-pe-text dark:text-white rounded-lg px-3 py-3 text-lg font-mono font-bold focus:outline-none focus:border-amber-400"
                                />
                                <span className="text-sm text-pe-muted font-bold">{currencySymbol}</span>
                            </div>
                            <p className="text-sm text-pe-muted mt-2 leading-snug">
                                {hjPool.targetReached
                                    ? `Vorschlag: DL ${formatMoney(m.earnedPoolDL, currencySymbol)} + VK ${formatMoney(m.earnedPoolVK, currencySymbol)} = ${formatMoney(m.earnedPool, currencySymbol)}.`
                                    : m.bisZielPolster > 0
                                      ? `Ziel nicht erreicht — Orientierungsgröße Polster: ${formatMoney(m.bisZielPolster, currencySymbol)}. Du entscheidest, was du auszahlst.`
                                      : 'Ziel nicht erreicht — Pool manuell setzen, wenn du trotzdem auszahlen willst.'}
                            </p>
                        </div>
                        <div className="sm:col-span-2 flex flex-wrap items-end gap-5 text-sm text-pe-muted dark:text-slate-400">
                            <div>
                                <span className="block text-xs uppercase tracking-widest font-bold mb-1">Ausgewählt</span>
                                <span className="text-pe-text dark:text-white font-bold text-base">{selectedCount} Personen</span>
                            </div>
                            <div>
                                <span className="block text-xs uppercase tracking-widest font-bold mb-1">Σ Grundgehalt / Monat</span>
                                <span className="text-pe-text dark:text-white font-bold font-mono text-base">
                                    {formatMoney(sumSelectedGrundgehalt, currencySymbol)}
                                </span>
                            </div>
                            <p className="text-sm leading-relaxed max-w-md">
                                Jeder bekommt seinen Anteil am Pool nach Grundgehalt.
                            </p>
                        </div>
                    </div>

                    <div className="px-6 py-4 space-y-6">
                        {grouped.length === 0 ? (
                            <p className="text-base text-pe-muted italic text-center py-10">Keine Mitarbeiter vorhanden.</p>
                        ) : (
                            grouped.map((group) => (
                                <div key={group.source}>
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-sm font-black uppercase tracking-widest text-amber-600 dark:text-amber-400">
                                            {group.label}
                                        </h3>
                                        <div className="flex gap-3">
                                            <button
                                                type="button"
                                                onClick={() => selectAllInGroup(group.source, true)}
                                                className="text-xs font-bold uppercase tracking-widest text-pe-muted hover:text-amber-500"
                                            >
                                                Alle
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => selectAllInGroup(group.source, false)}
                                                className="text-xs font-bold uppercase tracking-widest text-pe-muted hover:text-pe-text"
                                            >
                                                Keine
                                            </button>
                                        </div>
                                    </div>
                                    <div className="space-y-1 rounded-xl border border-pe-border dark:border-slate-800 overflow-hidden">
                                        {group.list.map((row) => (
                                            <label
                                                key={row.id}
                                                className={`flex items-center gap-3 px-3 py-3 cursor-pointer transition-colors ${
                                                    row.selected
                                                        ? 'bg-amber-500/10 border-l-2 border-amber-500'
                                                        : 'bg-pe-tile-header/40 dark:bg-[#15171e]/60 border-l-2 border-transparent hover:bg-pe-elevated dark:hover:bg-slate-900/40'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={row.selected}
                                                    onChange={() => toggleMember(row.id)}
                                                    className="h-4 w-4 rounded border-pe-border-strong dark:border-slate-600 bg-pe-input text-amber-500 focus:ring-amber-500/40"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-base font-bold text-pe-text dark:text-white truncate">
                                                        {row.name}
                                                    </div>
                                                    <div className="text-sm text-pe-muted dark:text-slate-500 truncate">
                                                        {row.taetigkeit || '—'} · Gehalt {formatMoney(row.grundgehalt, currencySymbol)}
                                                    </div>
                                                </div>
                                                <div className="text-right shrink-0 min-w-[7.5rem]">
                                                    <div className="text-xs text-pe-muted font-mono mb-0.5">{row.sharePercent.toFixed(1)} %</div>
                                                    <div className={`text-lg font-bold font-mono ${row.selected ? 'text-amber-500' : 'text-pe-muted'}`}>
                                                        {formatMoney(row.calculatedBonusMonthly, currencySymbol)}
                                                    </div>
                                                    <div className="text-xs text-pe-muted font-mono">/ Monat</div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    <div className="px-6 py-4 border-t border-pe-border dark:border-slate-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-pe-tile-header dark:bg-[#15171e] rounded-b-2xl sticky bottom-0">
                        <p className="text-xs text-pe-muted leading-snug max-w-md">
                            Speichert nur die Team-Bonus-Vorschau (Pool &amp; Auswahl). Schreibt nichts in Personaldaten
                            oder die laufende Kalkulation.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-5 py-2.5 rounded-lg border border-pe-border dark:border-slate-700 text-pe-muted text-sm font-bold uppercase tracking-widest hover:text-pe-text"
                            >
                                Schließen
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    commitPool();
                                    applyShares();
                                    onClose();
                                }}
                                className="px-5 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-black uppercase tracking-widest shadow-lg"
                            >
                                Vorschau speichern
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PETeamBonusModal;
